"use client";

import { useState, useRef, useCallback } from "react";
import Image from "next/image";
import {
  X,
  Upload,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  RefreshCw,
  Sparkles,
  Camera,
  User,
  ArrowRight,
} from "lucide-react";
import { useAction } from "convex/react";
import { api } from "@/convex/_generated/api";

/** Photo validation result returned by the virtual try-on validation action. */
interface PhotoValidationResult {
  isValid: boolean;
  score?: number;
  criteria?: any;
  reasons: string[];
  suggestions?: string[];
}

interface VirtualTryOnModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  product: {
    _id: string;
    name: string;
    images: string[];
    description?: string;
  };
}

type ModalStep = "upload" | "validating" | "validation-failed" | "generating" | "result";

export function VirtualTryOnModal({ isOpen, onClose, product, brandId }: VirtualTryOnModalProps) {
  const validateTryOnPhoto = useAction(api.products.productImageAI.validateTryOnPhoto);
  const generateTryOnAction = useAction(api.products.productImageAI.generateTryOn);
  const [step, setStep] = useState<ModalStep>("upload");
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<PhotoValidationResult | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetModal = useCallback(() => {
    setStep("upload");
    setUserPhoto(null);
    setValidationResult(null);
    setGeneratedImage(null);
    setError(null);
  }, []);

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      setUserPhoto(base64);
      setStep("validating");
      await validatePhoto(base64);
    };
    reader.readAsDataURL(file);
    if (e.target) e.target.value = "";
  };

  const validatePhoto = async (photoBase64: string) => {
    try {
      const result = (await validateTryOnPhoto({
        photoBase64,
        brandId: brandId as any,
      })) as PhotoValidationResult;
      setValidationResult(result);

      if (result.isValid) {
        setStep("generating");
        await generateTryOn(photoBase64);
      } else {
        setStep("validation-failed");
      }
    } catch (err: any) {
      console.error("Validation error:", err);
      setError(err?.message || "Failed to validate photo. Please try again.");
      setStep("validation-failed");
    }
  };

  const generateTryOn = async (photoBase64: string) => {
    try {
      const result = await generateTryOnAction({
        userPhotoBase64: photoBase64,
        productImages: product.images,
        productName: product.name,
        productDescription: product.description,
        brandId: brandId as any,
      });

      if (!result.success) {
        setError(result.error || "Generation failed");
        setStep("validation-failed");
        return;
      }

      if (result.imageBase64) {
        setGeneratedImage(`data:image/jpeg;base64,${result.imageBase64}`);
        setStep("result");
      } else {
        setError("No image was generated. Please try again.");
        setStep("validation-failed");
      }
    } catch (err: any) {
      console.error("Generation error:", err);
      setError(err?.message || "Failed to generate try-on image. Please try again.");
      setStep("validation-failed");
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement("a");
    link.href = generatedImage;
    link.download = `virtual-tryon-${product.name.replace(/\s+/g, "-").toLowerCase()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRetry = () => {
    setStep("upload");
    setUserPhoto(null);
    setValidationResult(null);
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-6">
      {/* Backdrop - only closes on upload step */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={step === "upload" ? handleClose : undefined}
      />
      
      {/* Modal Container - LARGE and responsive */}
      <div
        className="relative bg-white w-full max-w-4xl max-h-[95vh] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-black text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6" />
            <div>
              <h3 className="font-bold uppercase tracking-wider">Virtual Try-On</h3>
              <p className="text-sm text-gray-300">{product.name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* UPLOAD STEP */}
          {step === "upload" && (
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                {/* Left: Product Preview */}
                <div className="space-y-4">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500">Product to Try</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">
                    <Image
                      src={product.images[0] || "/placeholder.png"}
                      alt={product.name}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <p className="text-center font-medium">{product.name}</p>
                </div>

                {/* Right: Upload Area */}
                <div className="space-y-4">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500">Your Photo</h4>
                  
                  {/* Guidelines */}
                  <div className="bg-gray-50 border border-gray-200 p-4 space-y-2">
                    <p className="font-medium text-sm flex items-center gap-2">
                      <Camera className="w-4 h-4" /> Photo Requirements
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        Full body visible (head to toe)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        Face clearly visible
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        Standing in neutral pose
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        Good lighting
                      </li>
                    </ul>
                  </div>

                  {/* Upload Button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full aspect-[3/4] border-2 border-dashed border-gray-300 hover:border-purple-500 transition-colors flex flex-col items-center justify-center gap-4 bg-gray-50 hover:bg-purple-50 group"
                  >
                    <div className="w-20 h-20 rounded-full bg-gray-200 group-hover:bg-purple-100 flex items-center justify-center transition-colors">
                      <User className="w-10 h-10 text-gray-400 group-hover:text-purple-500" />
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-lg">Upload Your Photo</p>
                      <p className="text-sm text-gray-500 mt-1">Click to select or drag & drop</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Upload className="w-4 h-4" />
                      <span>JPG, PNG up to 10MB</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* VALIDATING STEP */}
          {step === "validating" && (
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                {/* Left: Product */}
                <div className="space-y-4">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500">Product</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">
                    <Image src={product.images[0] || "/placeholder.png"} alt={product.name} fill className="object-cover" />
                  </div>
                </div>
                {/* Right: User Photo with overlay */}
                <div className="space-y-4">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500">Your Photo</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">
                    {userPhoto && <Image src={userPhoto} alt="Your photo" fill className="object-cover" />}
                    <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white">
                      <Loader2 className="w-12 h-12 animate-spin mb-4" />
                      <p className="font-bold text-lg">Analyzing Photo...</p>
                      <p className="text-sm text-gray-300 mt-1">Checking requirements</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VALIDATION FAILED STEP */}
          {step === "validation-failed" && (
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                {/* Left: Product */}
                <div className="space-y-4">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500">Product</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">
                    <Image src={product.images[0] || "/placeholder.png"} alt={product.name} fill className="object-cover" />
                  </div>
                </div>
                {/* Right: User Photo + Error */}
                <div className="space-y-4">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500">Your Photo</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">
                    {userPhoto && <Image src={userPhoto} alt="Your photo" fill className="object-cover" />}
                  </div>
                  {/* Error Box */}
                  <div className="bg-red-50 border border-red-200 p-4 space-y-3">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-5 h-5" />
                      <h4 className="font-bold text-sm">Photo Doesn't Meet Requirements</h4>
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    {validationResult && !validationResult.isValid && validationResult.reasons.length > 0 && (
                      <ul className="text-sm text-red-600 space-y-1">
                        {validationResult.reasons.map((r, i) => (
                          <li key={i} className="flex items-start gap-2"><X className="w-4 h-4 flex-shrink-0 mt-0.5" />{r}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button onClick={handleRetry} className="w-full h-12 bg-black text-white uppercase tracking-widest font-medium text-sm flex items-center justify-center gap-2 hover:bg-zinc-800">
                    <RefreshCw className="w-4 h-4" /> Try Another Photo
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* GENERATING STEP - Large side by side with animation */}
          {step === "generating" && (
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-4 md:gap-6 items-center">
                {/* Left: User Photo */}
                <div className="space-y-3">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500 text-center">Your Photo</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden border-2 border-gray-200">
                    {userPhoto && <Image src={userPhoto} alt="Your photo" fill className="object-cover" />}
                  </div>
                </div>

                {/* Center Arrow Animation */}
                <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="bg-purple-600 text-white p-4 rounded-full animate-pulse shadow-lg">
                    <ArrowRight className="w-8 h-8" />
                  </div>
                </div>

                {/* Right: Generating Preview */}
                <div className="space-y-3">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500 text-center">Try-On Result</h4>
                  <div className="relative aspect-[3/4] bg-gradient-to-br from-purple-100 to-purple-200 overflow-hidden border-2 border-purple-300">
                    {/* Product ghost image */}
                    <Image src={product.images[0] || "/placeholder.png"} alt={product.name} fill className="object-cover opacity-20" />
                    {/* Generating overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 text-center shadow-xl">
                        <div className="relative w-16 h-16 mx-auto mb-4">
                          <Sparkles className="w-16 h-16 text-purple-600 animate-pulse" />
                          <div className="absolute inset-0 animate-spin" style={{ animationDuration: "3s" }}>
                            <div className="w-3 h-3 bg-purple-400 rounded-full absolute top-0 left-1/2 -translate-x-1/2" />
                          </div>
                        </div>
                        <p className="font-bold text-lg text-gray-800">Creating Your Look</p>
                        <p className="text-sm text-gray-500 mt-1">AI is dressing you in {product.name}</p>
                        <div className="mt-4 flex justify-center gap-1">
                          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Arrow */}
              <div className="md:hidden flex justify-center my-4">
                <div className="bg-purple-600 text-white p-3 rounded-full animate-pulse">
                  <ArrowRight className="w-6 h-6 rotate-90" />
                </div>
              </div>
            </div>
          )}

          {/* RESULT STEP - Side by side comparison */}
          {step === "result" && generatedImage && (
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                {/* Left: Original Photo */}
                <div className="space-y-3">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500 text-center">Before</h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden border-2 border-gray-200">
                    {userPhoto && <Image src={userPhoto} alt="Your original photo" fill className="object-cover" />}
                  </div>
                </div>

                {/* Right: Generated Result */}
                <div className="space-y-3">
                  <h4 className="font-bold text-sm uppercase tracking-wider text-purple-600 text-center flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" /> After - Virtual Try-On
                  </h4>
                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden border-2 border-purple-400">
                    <Image src={generatedImage} alt="Virtual Try-On Result" fill className="object-cover" />
                    <div className="absolute top-3 right-3 bg-purple-600 text-white text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 shadow-lg">
                      <Sparkles className="w-3.5 h-3.5" /> AI Generated
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-4">
                <button
                  onClick={handleRetry}
                  className="flex-1 h-14 border-2 border-gray-300 text-black uppercase tracking-widest font-medium text-sm flex items-center justify-center gap-2 hover:border-black transition-colors"
                >
                  <RefreshCw className="w-5 h-5" /> Try Again
                </button>
                <button
                  onClick={handleDownload}
                  className="flex-1 h-14 bg-black text-white uppercase tracking-widest font-medium text-sm flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors"
                >
                  <Download className="w-5 h-5" /> Download
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center mt-4">
                This is an AI-generated preview. Actual product fit may vary.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
